import { ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Play } from 'lucide-react';
import { toast } from 'sonner';
import WizardFooter from '../components/WizardFooter';
import useFieldErrors from '../useFieldErrors';
import {
  missingForFiles,
  missingForSignoff,
  Missing,
} from '../utils/validation';
import { cn } from '@/lib/utils';
import { getYouTubeEmbedUrl } from '@/lib/youtube';
import { useGetCategoriesQuery } from '@/redux/category/category.api.slice';
import { ICategoryType } from '@/redux/category/category.d';
import { IJobType } from '@/redux/post/post.d';
import {
  selectV2,
  setStep,
  setFocusField,
  setMultiStage,
  setSubmitted,
  setSubmitting,
} from '../v2Upload.slice';
import { READONLY_REASON, FocusTarget } from '../utils/reviewTargets';
import { STEP_LABELS } from '../stepLabels';
import { plural } from '../utils/fileHelpers';
import { jobSizeMatchesCam, designSwMatchesCam } from '../utils/mergeSources';
import useDraftSync from '../useDraftSync';
import {
  buildBasicsMetaPatch,
  buildJobDetailsPatch,
  buildModelGroups,
  extractErrorMessage,
  fileAttachmentIdMap,
} from '../utils/postSync';
import ToolTable from '../components/ToolTable';
import FileCard from '../components/FileCard';
import ControllerLogo from '../components/ControllerLogo';
import SoftwareLogo from '../components/SoftwareLogo';
import {
  CARD_SHELL,
  FIELD_LABEL,
  STEP_HEADING,
} from '../components/fileCardStyles';
import { V2Basics, V2File, V2JobDetails, V2Operation } from '../v2Upload.d';

/** An inset ring, not an outline — the review grid is `overflow-hidden`, which
 * would clip an outline drawn outside the cell. */
const CELL_BAD = 'ring-1 ring-inset ring-destructive/60 bg-destructive/[0.05]';

/** Review key → the step and field that owns it. `field` doubles as the
 * `Missing.field` its value comes from, so a gap named by the earlier steps'
 * rules redlines the cell showing it here. A key absent from this map is greyed
 * — the wizard set it, not the user. */
const REVIEW_TARGET: Record<string, FocusTarget> = {
  'PROJECT NAME': { step: 1, field: 'title' },
  DESCRIPTION: { step: 1, field: 'description' },
  CATEGORY: { step: 1, field: 'category' },
  DIFFICULTY: { step: 1, field: 'difficulty' },
  TAGS: { step: 1, field: 'tags' },
  MATERIAL: { step: 4, field: 'material' },
  LICENSE: { step: 5, field: 'license-presets' },
  'JOB SIZE': { step: 4, field: 'jobSize' },
  'JOB TYPE': { step: 4, field: 'jobType' },
  'FLIP DIRECTION': { step: 4, field: 'flipDirection' },
  'Z-ZERO': { step: 4, field: 'zZero' },
  'XY DATUM': { step: 4, field: 'xyDatum' },
  'DESIGN SW': { step: 4, field: 'software' },
  'TOOL SETTER': { step: 4, field: 'toolSetter' },
  ATC: { step: 4, field: 'atc' },
};

/** "CRV"/"ART" beside a field the dropped design file filled in, so a seeded
 * value reads differently from one the user typed. */
function SourceBadge({ badge }: { badge?: string }) {
  if (!badge) return null;
  return (
    <span className="rounded-full border border-input px-1.5 py-px text-[10px] font-semibold text-muted-foreground">
      {badge}
    </span>
  );
}

/** The `data-focus` + red-ring props a review cell needs when its field is a
 * gap. Spread onto the cell's own root. */
interface CellFlag {
  focus?: string;
  invalid?: boolean;
}

/** One row of the review grid. */
interface ReviewRow {
  k: string;
  v: string;
  /** Full-width, for the values that don't fit a third of the grid. */
  wide?: boolean;
  multiline?: boolean;
  /** Only for a greyed row — the editable ones take theirs from REVIEW_TARGET. */
  focus?: string;
}

/** YouTube's own still for a video, no API key — same source VideoLinks uses on
 * Add files, so the card here matches the one where it was added. */
function videoThumb(link: string): string | null {
  const id = getYouTubeEmbedUrl(link)?.split('/').pop();
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
}

/** Sign off is the wizard's fifth step, and `currentPage` is 1-based — the same
 * numbering useDraftSync's PAGE_AFTER_* constants advance through. It has to be
 * in the submit payload, not just implied: `createPostDto` validates the PATCH
 * BODY with `presence: 'required'`, so an omitted `currentPage` fails the
 * completeness gate and the publish 400s. */
const PAGE_SIGNOFF = 5;

/** The final metadata patch. Title/description are plain strings the update
 * takes directly; category resolves to an id (skipped when unknown so a stale
 * name can't 400 the patch); jobDetails, material/licence and modelGroups are
 * all re-sent, which is what makes an edit made by jumping back land at all —
 * the rail can return here without that step's own Continue ever running.
 *
 * `publish: true` is what makes this a SUBMISSION rather than one more
 * autosave. The backend keys every submission side effect off that one flag
 * (post.service.ts's updatePost): visibility → private so it enters the
 * moderation queue, the "submitted for review" email, the attachment ZIP, and
 * the project-group privatisation. Without it the patch is an ordinary save —
 * the wizard showed "Project has been submitted for review 🎉" while the post
 * stayed a draft that no moderator would ever see. v1 has always sent it
 * (PostUpload/index.tsx's handlePublish); v2 was the odd one out. */
function buildFinalPatch(
  basics: V2Basics,
  jobDetails: V2JobDetails,
  categoryId: string | undefined,
  tagIds: string[],
  modelGroups: ReturnType<typeof buildModelGroups>,
  publish: boolean
): Record<string, unknown> {
  return {
    title: basics.title,
    description: basics.description,
    ...(categoryId ? { category: categoryId } : {}),
    tags: tagIds,
    ...buildJobDetailsPatch(jobDetails),
    ...buildBasicsMetaPatch(basics),
    ...(modelGroups.length > 0 ? { modelGroups } : {}),
    currentPage: PAGE_SIGNOFF,
    ...(publish ? { publish: true } : {}),
  };
}

/** A review cell nothing can change: greyed, with a reason in its tooltip —
 * UNITS comes from the G-code, CONTROLLERS from where each file is placed. */
function GreyedCell({
  label,
  value,
  reason,
  className,
  focus,
  children,
}: {
  label: string;
  value?: string;
  reason?: string;
  className?: string;
  focus?: string;
  children?: ReactNode;
}) {
  return (
    <div
      data-focus={focus}
      title={reason ?? "Read from your G-code — can't be edited"}
      className={cn('p-3 flex flex-col gap-1 bg-muted/50', className)}
    >
      <span className={cn(FIELD_LABEL, 'text-muted-foreground/70')}>
        {label}
      </span>
      {children ?? (
        <span className="text-xs font-medium text-muted-foreground">
          {value}
        </span>
      )}
    </div>
  );
}

/** The value as it was answered. */
function CellValue({
  multiline,
  value,
}: {
  multiline?: boolean;
  value: string;
}) {
  if (multiline) {
    return (
      <div className="text-xs text-muted-foreground mt-1 whitespace-pre-line">
        {value}
      </div>
    );
  }
  return <div className="font-semibold text-sm mt-0.5">{value}</div>;
}

/** Anything on this step that jumps to where it is edited, on double-click or
 * Enter: every review cell, plus the tool-group and attachment cards. */
function JumpWrapper({
  target,
  onJump,
  ariaLabel,
  className,
  children,
  focus,
  invalid,
}: {
  target: FocusTarget;
  onJump: (t: FocusTarget) => void;
  /** Only where the content isn't the name — a card showing a thumbnail. A
   * review cell must keep its own label and value as its accessible name. */
  ariaLabel?: string;
  className?: string;
  children: ReactNode;
} & CellFlag) {
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      title={`Double-click to edit in ${STEP_LABELS[target.step]}`}
      data-focus={focus}
      data-invalid={invalid || undefined}
      onDoubleClick={() => onJump(target)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onJump(target);
      }}
      className={cn('cursor-pointer', className)}
    >
      {children}
    </div>
  );
}

/** One reviewed field, as answered. Nothing is edited here — a double-click
 * sends you to the step that owns it and rings the control there. */
function ReviewCell({
  label,
  value,
  multiline,
  target,
  onJump,
  className,
  sourceBadge,
  focus,
  invalid,
  children,
}: {
  label: string;
  value: string;
  multiline?: boolean;
  target: FocusTarget;
  onJump: (t: FocusTarget) => void;
  className?: string;
  sourceBadge?: string;
  children?: ReactNode;
} & CellFlag) {
  return (
    <JumpWrapper
      target={target}
      onJump={onJump}
      focus={focus}
      invalid={invalid}
      className={cn(
        'p-3 flex flex-col gap-1 bg-card transition-colors hover:bg-muted/40',
        invalid && CELL_BAD,
        className
      )}
    >
      <span className="flex items-center gap-1.5">
        <span className={FIELD_LABEL}>{label}</span>
        <SourceBadge badge={sourceBadge} />
      </span>
      {children ?? <CellValue multiline={multiline} value={value} />}
    </JumpWrapper>
  );
}

/** The design software as picked, each under its own brand mark. */
function SoftwareList({ value }: { value: string[] }) {
  if (value.length === 0) return <CellValue value="—" />;
  return (
    <div className="mt-1 flex flex-wrap gap-2">
      {value.map((name) => (
        <span
          key={name}
          className="inline-flex items-center gap-2 rounded-full border border-input py-1 pl-1 pr-3 text-xs font-medium"
        >
          <SoftwareLogo name={name} className="h-5 w-5" />
          {name}
        </span>
      ))}
    </div>
  );
}

/** Every controller the project posts for, under its own brand mark. */
function ControllerList({ controllers }: { controllers: string[] }) {
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-3">
      {controllers.map((c) => (
        <span
          key={c}
          className="inline-flex items-center gap-2 text-sm font-medium"
        >
          <ControllerLogo name={c} className="w-5 h-5" /> {c}
        </span>
      ))}
    </div>
  );
}

/** '—' for an empty value, so a blank field reads as "nothing set", not a gap. */
const dash = (s: string): string => s || '—';
const yesNo = (b: boolean): string => (b ? 'Yes' : 'No');
const unitLabel = (units: string): string => (units === 'Inches' ? 'in' : 'mm');

/** Job size as one line, or '—' until every dimension is set. Reads the string
 * form fields directly — no Number(), that's the wire-format path.
 *
 * Rotary stock is a cylinder, so it reads as length × diameter — the same split
 * the Job details step and `createPostDto` both make. Reading
 * width/height/thickness for it would show '—' for every rotary project even
 * with its size filled in. */
function jobSizeText(jd: V2JobDetails): string {
  const rotary = jd.jobType === IJobType.ROTARY;
  const dims = rotary
    ? [jd.length, jd.diameter]
    : [jd.width, jd.height, jd.thickness];
  if (dims.some((d) => !d)) return '—';

  const unit = unitLabel(jd.units);
  return rotary
    ? `${jd.length} long × ⌀${jd.diameter} ${unit}`
    : `${dims.join(' × ')} ${unit}`;
}

/** The review rows: Basics always, plus the parser/job-setup rows once there's
 * G-code. Order is the on-screen order; what's reviewed is what will be posted. */
function buildReviewItems(
  basics: V2Basics,
  jd: V2JobDetails,
  controllers: string[],
  hasGcode: boolean
): ReviewRow[] {
  const rows: ReviewRow[] = [
    { k: 'PROJECT NAME', v: dash(basics.title), wide: true },
    {
      k: 'DESCRIPTION',
      v: dash(basics.description),
      wide: true,
      multiline: true,
    },
  ];
  // Every controller the project posts for, not just the one read out of the
  // file the parser happened to open — a project is usually the same cut
  // posted for several machines.
  if (hasGcode) {
    rows.push({
      k: 'CONTROLLERS',
      v: dash(controllers.join(' · ')),
      wide: true,
      focus: 'review-readonly',
    });
  }
  rows.push(
    { k: 'CATEGORY', v: dash(basics.category) },
    { k: 'DIFFICULTY', v: dash(basics.difficulty) },
    { k: 'MATERIAL', v: dash(basics.material) },
    { k: 'LICENSE', v: dash(basics.license) },
    { k: 'TAGS', v: dash(basics.tags.join(' · ')) }
  );
  if (!hasGcode) return rows;
  rows.push(
    { k: 'UNITS', v: dash(jd.units) },
    { k: 'JOB SIZE', v: jobSizeText(jd) },
    { k: 'JOB TYPE', v: dash(jd.jobType) }
  );
  // Only meaningful for a double sided job, and only then is it sent — so it
  // is only reviewed then. A row reading '—' on every single sided project
  // would be noise the user cannot act on.
  if (jd.jobType === IJobType.DOUBLE_SIDED) {
    rows.push({ k: 'FLIP DIRECTION', v: dash(jd.flipDirection) });
  }
  rows.push(
    { k: 'Z-ZERO', v: dash(jd.zZeroPosition) },
    { k: 'XY DATUM', v: dash(jd.xyDatumPosition) },
    { k: 'DESIGN SW', v: dash(jd.softwareUsed.join(' · ')), wide: true },
    { k: 'TOOL SETTER', v: yesNo(jd.toolSetter) },
    { k: 'ATC', v: yesNo(jd.autoToolChanger) }
  );
  return rows;
}

function isStillAnalyzing(parsedReady: boolean, fileCount: number): boolean {
  return !parsedReady && fileCount > 0;
}

/** The banner while the longest G-code file is still parsing — you can submit
 * anyway and it finishes in the background. */
function AnalyzingBanner({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <div className="flex items-center gap-3 border border-primary/30 bg-accent rounded-xl p-3.5 mb-4">
      <span className="w-3.5 h-3.5 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      <div className="flex-1">
        <div className="text-xs font-semibold text-primary">
          Still reading your longest file
        </div>
        <div className="text-[11px] text-muted-foreground">
          Tool specs will fill in below. You can submit now — we&apos;ll finish
          in the background.
        </div>
      </div>
    </div>
  );
}

/** N photos · M CAD/PDF · K videos. */
function AttachmentCount({
  images,
  docs,
  videos,
}: {
  images: number;
  docs: number;
  videos: number;
}) {
  return (
    <span className="text-[11px] font-tertiary text-muted-foreground">
      {images} {plural(images, 'photo', 'photos')} · {docs} CAD/PDF
      {videos > 0 && ` · ${videos} ${plural(videos, 'video', 'videos')}`}
    </span>
  );
}

function PhotosGrid({
  images,
  thumbnailFileId,
  onJump,
}: {
  images: V2File[];
  thumbnailFileId: string | null;
  onJump: (t: FocusTarget) => void;
}) {
  if (images.length === 0) return null;
  return (
    <div className="mb-4">
      <div className={cn(FIELD_LABEL, 'mb-2')}>Photos</div>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
        {images.map((file) => (
          <JumpWrapper
            key={file.id}
            target={{ step: 2, field: 'images' }}
            onJump={onJump}
            ariaLabel={`Edit ${file.name} in Add files`}
            className="relative"
          >
            <FileCard file={file} readOnlyName />
            {file.id === thumbnailFileId && (
              <span className="absolute top-1.5 left-1.5 rounded-md bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-white">
                THUMBNAIL
              </span>
            )}
          </JumpWrapper>
        ))}
      </div>
    </div>
  );
}

function DocsGrid({
  docs,
  spaced,
  onJump,
}: {
  docs: V2File[];
  spaced: boolean;
  onJump: (t: FocusTarget) => void;
}) {
  if (docs.length === 0) return null;
  return (
    <div className={spaced ? 'mb-4' : ''}>
      <div className={cn(FIELD_LABEL, 'mb-2')}>CAD files &amp; docs</div>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
        {docs.map((file) => (
          <JumpWrapper
            key={file.id}
            target={{ step: 2, field: 'docs' }}
            onJump={onJump}
            ariaLabel={`Edit ${file.name} in Add files`}
          >
            <FileCard file={file} readOnlyName />
          </JumpWrapper>
        ))}
      </div>
    </div>
  );
}

function VideosGrid({
  videoLinks,
  onJump,
}: {
  videoLinks: string[];
  onJump: (t: FocusTarget) => void;
}) {
  if (videoLinks.length === 0) return null;
  return (
    <div>
      <div className={cn(FIELD_LABEL, 'mb-2')}>Videos</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {videoLinks.map((url) => {
          const thumb = videoThumb(url);
          return (
            <JumpWrapper
              key={url}
              target={{ step: 2, field: 'videos' }}
              onJump={onJump}
              ariaLabel="Edit video in Add files"
              className={CARD_SHELL}
            >
              <div className="relative aspect-video bg-black flex items-center justify-center overflow-hidden">
                {thumb && (
                  <img
                    src={thumb}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                )}
                <span className="absolute w-9 h-9 rounded-full bg-black/55 flex items-center justify-center pointer-events-none">
                  <Play className="w-4 h-4 text-white fill-white" />
                </span>
              </div>
              <div className="p-2.5">
                <span
                  title={url}
                  className="block truncate text-[10px] font-tertiary text-muted-foreground"
                >
                  {url}
                </span>
              </div>
            </JumpWrapper>
          );
        })}
      </div>
    </div>
  );
}

/** The read-only photos, CAD/docs and video links — double-click any to edit it
 * back on Add files. Hidden when there is nothing to show and nothing to flag.
 *
 * `fileGaps` is what a file kind deleted after Add files looks like here: its
 * grid is simply absent, so this section is the only thing on the step that can
 * say it is gone. */
function AttachmentsSection({
  images,
  docs,
  videoLinks,
  thumbnailFileId,
  onJump,
  fileGaps,
}: {
  images: V2File[];
  docs: V2File[];
  videoLinks: string[];
  thumbnailFileId: string | null;
  onJump: (t: FocusTarget) => void;
  fileGaps: Missing[];
}) {
  const empty = [images, docs, videoLinks].every((a) => a.length === 0);
  if (empty && fileGaps.length === 0) return null;
  return (
    <div className="mb-6" data-focus="attachments">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">Attachments</h3>
        <AttachmentCount
          images={images.length}
          docs={docs.length}
          videos={videoLinks.length}
        />
      </div>
      {fileGaps.length > 0 && (
        <button
          type="button"
          data-focus="dropzone"
          data-invalid
          onClick={() => onJump({ step: 2, field: 'dropzone' })}
          className="mb-4 flex w-full flex-col gap-1 rounded-xl border border-destructive/60 bg-destructive/[0.05] p-3 text-left"
        >
          {fileGaps.map((m) => (
            <span
              key={m.label}
              className="text-xs font-medium text-destructive"
            >
              Add {m.label} — go back to Add files
            </span>
          ))}
        </button>
      )}
      <PhotosGrid
        images={images}
        thumbnailFileId={thumbnailFileId}
        onJump={onJump}
      />
      <DocsGrid docs={docs} spaced={videoLinks.length > 0} onJump={onJump} />
      <VideosGrid videoLinks={videoLinks} onJump={onJump} />
    </div>
  );
}

/** The tool table as filled in on Tools, read-only — cut order, operation, file,
 * controller, bit. Hidden when there is no G-code. */
function ToolGroupsSection({
  operations,
  files,
}: {
  operations: V2Operation[];
  files: V2File[];
}) {
  if (operations.length === 0) return null;
  return (
    <div className="mb-6" data-focus="review-tools">
      <h3 className="mb-3 text-sm font-semibold">
        Bits &amp; lasers, in cut order
      </h3>
      <ToolTable operations={operations} files={files} readOnly />
    </div>
  );
}

// fallow-ignore-next-line complexity
export default function SignoffStep() {
  const dispatch = useDispatch();
  const {
    basics,
    files,
    thumbnailFileId,
    jobDetails,
    operations,
    parsedReady,
    submitting,
    videoLinks,
    camExtracted,
    mode,
  } = useSelector(selectV2);
  const inlineMulti = mode === 'multi';

  // "CRV"/"ART" beside a field the design file actually filled in — and gone
  // the moment a hand edit here makes that no longer true.
  const jobSizeBadge = jobSizeMatchesCam(jobDetails, camExtracted)
    ? camExtracted?.format.toUpperCase()
    : undefined;
  const designSwBadge = designSwMatchesCam(
    jobDetails.softwareUsed,
    camExtracted
  )
    ? camExtracted?.format.toUpperCase()
    : undefined;

  const { ensureDraftId, patchPost, resolveTagIds } = useDraftSync();
  const { data: categories } = useGetCategoriesQuery({
    type: ICategoryType.POST,
  });

  const jumpTo = (target: FocusTarget) => {
    dispatch(setFocusField(target.field));
    dispatch(setStep(target.step));
  };

  const images = files.filter((f) => f.kind === 'image');
  const docs = files.filter((f) => f.kind === 'doc');
  const controllers = [
    ...new Set(
      files.filter((f) => f.kind === 'gcode').map((f) => f.controller)
    ),
  ];
  const hasGcode = files.some((f) => f.kind === 'gcode');
  // The rail jumps straight here, so every earlier gate is re-run
  const missing = missingForSignoff(basics, files, operations, jobDetails);
  const { bad, guard } = useFieldErrors(missing);
  const reviewItems = buildReviewItems(
    basics,
    jobDetails,
    controllers,
    hasGcode
  );

  // The update wants the category's id, not its name, so resolve it — skipped
  // when unknown so a stale name can't 400 the patch.
  const categoryIdFor = (name: string): string | undefined =>
    (categories ?? []).find((c) => c.name === name)?._id;

  // fallow-ignore-next-line complexity
  const handleSubmit = async () => {
    if (!guard()) return;
    dispatch(setSubmitting(true));
    try {
      // Everything is re-sent: the rail can reach this step without an earlier
      // step's Continue ever running its own sync.
      const draftId = await ensureDraftId();
      const modelGroups = buildModelGroups(
        operations,
        fileAttachmentIdMap(files)
      );
      const tagIds = await resolveTagIds();
      await patchPost(
        draftId,
        buildFinalPatch(
          basics,
          jobDetails,
          categoryIdFor(basics.category),
          tagIds,
          modelGroups,
          !inlineMulti
        )
      );
      if (inlineMulti) {
        dispatch(setSubmitting(false));
        dispatch(setMultiStage('interstitial'));
      } else {
        dispatch(setSubmitted(true));
      }
    } catch (err) {
      toast.error(inlineMulti ? 'Save failed' : 'Submission failed', {
        description: extractErrorMessage(
          err,
          `Something went wrong while ${
            inlineMulti ? 'saving' : 'submitting'
          }. Please try again.`
        ),
      });
      dispatch(setSubmitting(false));
    }
  };

  // "CRV"/"ART", and the cells whose value is a row of brand marks rather than
  // a string. Lookups, so the grid stays a lookup and not a switch.
  const badges: Record<string, string | undefined> = {
    'JOB SIZE': jobSizeBadge,
    'DESIGN SW': designSwBadge,
  };
  const nodes: Record<string, ReactNode> = {
    CONTROLLERS: <ControllerList controllers={controllers} />,
    'DESIGN SW': <SoftwareList value={jobDetails.softwareUsed} />,
  };

  const renderReviewCell = (row: ReviewRow): ReactNode => {
    const target = REVIEW_TARGET[row.k];
    const className = row.wide ? 'col-span-3' : undefined;
    // UNITS and CONTROLLERS — nothing on any step sets them by hand.
    if (!target) {
      return (
        <GreyedCell
          key={row.k}
          label={row.k}
          value={row.v}
          reason={READONLY_REASON[row.k]}
          className={className}
          focus={row.focus}
        >
          {nodes[row.k]}
        </GreyedCell>
      );
    }
    return (
      <ReviewCell
        key={row.k}
        label={row.k}
        value={row.v}
        multiline={row.multiline}
        target={target}
        onJump={jumpTo}
        className={className}
        sourceBadge={badges[row.k]}
        focus={target.field}
        invalid={bad(target.field)}
      >
        {nodes[row.k]}
      </ReviewCell>
    );
  };

  return (
    <div className="flex flex-col min-h-full">
      <h2 className={STEP_HEADING}>
        {inlineMulti ? 'Review this workpiece' : 'Review & submit'}
      </h2>
      <p className="text-sm text-muted-foreground mb-6 max-w-[640px]">
        {inlineMulti
          ? "Here's this workpiece in one place. Double-click any field to go back and change it, then mark it complete."
          : "Here's your whole project in one place. Double-click any field to go back and change it, then submit."}
      </p>

      <AnalyzingBanner show={isStillAnalyzing(parsedReady, files.length)} />

      <div
        data-focus="review"
        className="grid grid-cols-3 gap-px bg-border border rounded-xl overflow-hidden mb-6"
      >
        {reviewItems.map(renderReviewCell)}
      </div>

      <ToolGroupsSection operations={operations} files={files} />

      <AttachmentsSection
        images={images}
        docs={docs}
        videoLinks={videoLinks}
        thumbnailFileId={thumbnailFileId}
        onJump={jumpTo}
        fileGaps={bad('dropzone') ? missingForFiles(files) : []}
      />

      <WizardFooter
        onBack={() => dispatch(setStep(5))}
        onContinue={handleSubmit}
        busy={submitting}
        busyLabel={inlineMulti ? 'Saving…' : 'Submitting…'}
        backDisabled={submitting}
        continueDisabled={false}
        continueLabel={inlineMulti ? 'Workpiece complete' : 'Submit for Review'}
        hint={
          <span className="text-xs text-muted-foreground font-tertiary">
            Reviewed by a moderator before it goes live
          </span>
        }
      />
    </div>
  );
}
